import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

export const PrivacyPolicy = ({ open, onOpenChange }) => {
  const lastUpdated = 'February 14, 2026';
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]" data-testid="privacy-policy-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading">Privacy Policy</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-[70vh] pr-4">
          <div className="prose prose-sm dark:prose-invert max-w-none space-y-6">
            <p className="text-muted-foreground">Last Updated: {lastUpdated}</p>
            
            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">1. Introduction</h2>
              <p>
                Welcome to Cash Forth ("we," "our," or "us"). We are committed to protecting your privacy 
                and personal information. This Privacy Policy explains how we collect, use, disclose, and 
                safeguard your information when you use our cash flow forecasting application and related 
                services (collectively, the "Service").
              </p>
              <p>
                By using the Service, you agree to the collection and use of information in accordance 
                with this Privacy Policy. If you do not agree with our policies and practices, please do 
                not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">2. Information We Collect</h2>
              
              <h3 className="text-base font-medium mt-4 mb-2">2.1 Information You Provide</h3>
              <p>We collect information you voluntarily provide when using the Service, including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Account Information:</strong> Email address, name, and password when you create an account through our authentication provider (Clerk)</li>
                <li><strong>Financial Data:</strong> Transaction descriptions, amounts, dates, and recurrence patterns you enter for cash flow forecasting</li>
                <li><strong>Settings:</strong> Your preferences including starting balance, alert thresholds, and display preferences</li>
                <li><strong>Workspace Data:</strong> Information about shared workspaces and invited team members</li>
                <li><strong>Billing Data (if you subscribe):</strong> Payment card details, billing address, and similar information are collected and stored directly by our payment processor, Stripe. Cash Forth does not receive or store your full card number, CVC, or bank account number. We receive and store only: your Stripe customer ID, your Stripe subscription ID, the plan (monthly / annual), the subscription status (e.g., active, past_due, canceled), and the current period end date.</li>
              </ul>

              <h3 className="text-base font-medium mt-4 mb-2">2.2 Information Collected Automatically</h3>
              <p>When you use the Service, we automatically collect certain information, including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Usage Data:</strong> Information about how you interact with the Service, including features used, pages visited, and actions taken</li>
                <li><strong>Device Information:</strong> Browser type, operating system, device type, and screen resolution</li>
                <li><strong>Log Data:</strong> IP address, access times, and referring URLs</li>
              </ul>

              <h3 className="text-base font-medium mt-4 mb-2">2.3 Guest Mode</h3>
              <p>
                If you use the Service without creating an account (Guest Mode), your data is stored 
                locally in your browser's localStorage. This data remains on your device and is not 
                transmitted to our servers unless you later create an account and choose to migrate 
                your data.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide, maintain, and improve the Service</li>
                <li>Generate cash flow forecasts and projections based on your transaction data</li>
                <li>Authenticate your identity and manage your account</li>
                <li>Enable collaboration features such as workspace sharing</li>
                <li>Send important notifications about the Service</li>
                <li>Analyze usage patterns to improve user experience</li>
                <li>Detect, prevent, and address technical issues and security threats</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">4. Analytics</h2>
              <p>
                We use <strong>PostHog</strong>, a product analytics platform, to understand how 
                users interact with our Service so we can fix bugs, improve flows, and prioritize 
                features. Our PostHog configuration is intentionally privacy-preserving:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>No cookies, no localStorage, no sessionStorage.</strong> PostHog is 
                  configured in <em>memory-only persistence</em> mode. Nothing is stored on your 
                  device for analytics purposes. An anonymous in-memory identifier is regenerated 
                  every time you reload the page.
                </li>
                <li>
                  <strong>No session recording.</strong> We do not record your screen, keystrokes, 
                  mouse movements, or form input.
                </li>
                <li>
                  <strong>Do Not Track is respected.</strong> If your browser sends a "Do Not Track" 
                  signal, PostHog will not send events at all.
                </li>
                <li>
                  <strong>No financial transaction data is sent.</strong> The amounts, descriptions, 
                  dates, and balances you enter into Cash Forth are never transmitted to PostHog. We 
                  send PostHog only metadata about <em>how</em> the Service is used.
                </li>
              </ul>
              <p>PostHog may receive the following event metadata:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Pages and features you visit or use</li>
                <li>Clicks and form interactions that we have explicitly instrumented as events</li>
                <li>Approximate geographic location (derived server-side from your IP address)</li>
                <li>Browser and operating system type</li>
                <li>Your Clerk user ID and email, but only after you sign in — so that we can attribute events to your account</li>
              </ul>
              <p>
                <strong>Legal basis (GDPR).</strong> We rely on our <em>legitimate interest</em> in 
                understanding how our Service is used to operate, secure, and improve it. Because no 
                data is stored on your device, no consent under the ePrivacy Directive is required, 
                and no cookie banner is presented. You can object to this analytics processing at any 
                time by enabling Do Not Track in your browser or by contacting us at 
                privacy@cashforth.com.
              </p>
              <p>
                You can learn more about PostHog's privacy practices at{' '}
                <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  posthog.com/privacy
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">5. Cookies and Local Storage</h2>
              
              <h3 className="text-base font-medium mt-4 mb-2">5.1 Cookies</h3>
              <p>
                We use only <strong>strictly necessary cookies</strong>. We do not use any tracking, 
                advertising, or analytics cookies. The cookies that are set when you use the Service 
                are:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Authentication (Clerk):</strong> When you sign in, our authentication 
                  provider Clerk sets cookies to maintain your signed-in session, protect against 
                  CSRF attacks, and keep you signed in across page loads. Without these cookies you 
                  cannot sign in.
                </li>
                <li>
                  <strong>Payments & fraud prevention (Stripe):</strong> When you visit our checkout 
                  page or Billing / Customer Portal, Stripe sets cookies on its own domains (e.g.,{' '}
                  <code>stripe.com</code>, <code>js.stripe.com</code>, <code>checkout.stripe.com</code>,{' '}
                  <code>billing.stripe.com</code>) for fraud detection, bot detection, and session 
                  management during the payment flow. These cookies are set and read by Stripe, not 
                  by Cash Forth, and only on payment-related pages.
                </li>
              </ul>
              <p>
                Both categories are exempt from cookie-consent requirements under the ePrivacy 
                Directive's "strictly necessary" carve-out — they are required to deliver services 
                you have explicitly requested (signing in, paying for a Subscription).
              </p>

              <h3 className="text-base font-medium mt-4 mb-2">5.2 Local Storage</h3>
              <p>We use your browser's localStorage for the following first-party purposes only:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Store your theme preference (light/dark mode) and currency display preference</li>
                <li>Store transaction and settings data in Guest Mode</li>
                <li>Remember dismissed notifications and UI preferences</li>
                <li>Temporarily stage Guest Mode data that is about to be migrated to the server after you subscribe</li>
              </ul>
              <p>
                We do <strong>not</strong> use localStorage for analytics or cross-site tracking. 
                You can clear localStorage data through your browser settings; note that clearing 
                Guest Mode data will permanently delete any unsaved transactions stored only in 
                your browser.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">6. Data Sharing and Disclosure</h2>
              <p>
                <strong>We do not sell your personal information.</strong> We do not use your 
                financial transaction data for advertising or share it with advertising networks.
              </p>
              <p>We share limited information with the following third-party service providers strictly so they can help us operate the Service:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Clerk</strong> — authentication and account management. Receives your email 
                  address, name (if you provide one), password (as a hash), and authentication events. 
                  Privacy:{' '}
                  <a href="https://clerk.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    clerk.com/legal/privacy
                  </a>.
                </li>
                <li>
                  <strong>Stripe</strong> — payment processing and subscription billing, including the 
                  Billing / Customer Portal. If you subscribe, Stripe receives your name, email, 
                  billing address, payment card details, IP address, and device/fraud signals directly 
                  from you. Cash Forth receives back only a Stripe customer ID, subscription ID, plan, 
                  status, and current-period-end date. Privacy:{' '}
                  <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    stripe.com/privacy
                  </a>.
                </li>
                <li>
                  <strong>PostHog</strong> — product analytics, in cookieless / memory-only 
                  configuration as described in Section 4. Receives event metadata (pages, clicks, 
                  device info, approximate location, and — only after sign-in — your Clerk user ID 
                  and email). Does not receive your financial transaction data. Privacy:{' '}
                  <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    posthog.com/privacy
                  </a>.
                </li>
                <li>
                  <strong>MongoDB Atlas</strong> — encrypted database hosting for the data you save 
                  to the Service (workspaces, transactions, settings, and subscription metadata). 
                  Privacy:{' '}
                  <a href="https://www.mongodb.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    mongodb.com/legal/privacy-policy
                  </a>.
                </li>
                <li>
                  <strong>Hosting / CDN providers</strong> — used to serve the Cash Forth web app. 
                  These providers process IP addresses and standard server log data incidentally to 
                  delivering the Service.
                </li>
              </ul>
              <p className="mt-4">We may also share information in the following limited circumstances:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Workspace Members:</strong> If you use shared workspaces, other workspace members can view and edit shared transaction data</li>
                <li><strong>Legal Requirements:</strong> We may disclose information if required by law or in response to valid legal requests</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, your information may be transferred</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">7. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. These 
                measures include:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Encryption of data in transit using HTTPS/TLS</li>
                <li>Secure authentication through Clerk</li>
                <li>Access controls and authentication for database systems</li>
                <li>Regular security assessments</li>
              </ul>
              <p>
                However, no method of transmission over the Internet or electronic storage is 100% secure. 
                While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">8. Data Retention & Account Deletion</h2>
              <p>
                We retain your personal information for as long as your account is active or as 
                needed to provide the Service. We may also retain certain information as necessary to 
                comply with legal obligations, resolve disputes, and enforce our agreements.
              </p>
              <p>
                <strong>When you delete your Cash Forth account</strong>, we will:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Delete your workspaces, transactions, and account settings from our database.</li>
                <li>Delete your subscription metadata stored on our servers (Stripe customer ID, subscription ID, status).</li>
                <li>Instruct Stripe to cancel any active subscription so you are not billed again.</li>
              </ul>
              <p>
                <strong>Data retained by third parties after deletion.</strong> Stripe is legally 
                required to retain certain billing, invoice, and anti-fraud records for financial and 
                tax compliance purposes; we do not control Stripe's retention of that data. Clerk may 
                similarly retain minimal authentication-related records as described in its privacy 
                policy. Server logs and backups may contain copies of data for a limited period before 
                they are rotated out.
              </p>
              <p>
                <strong>Guest Mode</strong> data stored in localStorage persists until you clear your 
                browser data or manually delete it. It is not transmitted to our servers unless you 
                subscribe and opt to migrate it.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">9. Your Rights and Choices</h2>
              <p>Depending on your location, you may have certain rights regarding your personal information:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate personal information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information and account</li>
                <li><strong>Export:</strong> Request a portable copy of your data</li>
                <li><strong>Opt-out:</strong> Opt out of certain data collection (e.g., analytics)</li>
              </ul>
              <p>
                To exercise these rights, please contact us using the information provided below. We will 
                respond to your request within a reasonable timeframe.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">10. Children's Privacy</h2>
              <p>
                The Service is not intended for children under the age of 13. We do not knowingly collect 
                personal information from children under 13. If we become aware that we have collected 
                personal information from a child under 13, we will take steps to delete such information 
                promptly.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">11. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than your country 
                of residence. These countries may have data protection laws that differ from your country. 
                By using the Service, you consent to the transfer of your information to these countries.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">12. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new Privacy Policy on this page and updating the "Last Updated" date. We 
                encourage you to review this Privacy Policy periodically.
              </p>
              <p>
                Your continued use of the Service after any changes to this Privacy Policy constitutes 
                your acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mt-6 mb-3">13. Contact Us</h2>
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our 
                privacy practices, please contact us at:
              </p>
              <p className="mt-2">
                <strong>Cash Forth</strong><br />
                Email: privacy@cashforth.com
              </p>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
